<template>
  <div
    :id="id"
    class="shelters-list"
  >
    <fn-loading-overlay v-if="isLoading" />
    <div class="sl-header">
      <fn-headline
        v-if="title"
        type="h2"
        no-margin-bottom
      >
        {{ title }}
      </fn-headline>
    </div>
    <div v-if="!shelters?.length && !isLoading">
      <fn-paragraph>
        {{ $t('shelters.overview.noShelters') }}
      </fn-paragraph>
    </div>
    <div class="sl-list">
      <div
        v-for="shelter in shelters"
        :key="shelter.wishListLink"
        class="shelter"
      >
        <div class="s-image">
          <fn-image
            :src="shelter.media?.url || fallbackImage"
            :alt="shelter.media?.altText"
            fit="contain"
          />
        </div>
        <div class="s-content">
          <fn-headline
            v-if="shelter.name"
            type="h2"
            size="h3"
            no-margin-bottom
          >
            {{ shelter.name }}
          </fn-headline>
          <fn-headline
            v-if="shelter.address"
            type="div"
            no-margin-bottom
          >
            {{ shelter.address.formattedAddress }}
          </fn-headline>
          <fn-paragraph class="c-text trim">
            {{ shelter.description || $t('shelters.overview.noDescription') }}
          </fn-paragraph>
          <fn-button
            v-if="shelter.detailPagePath"
            class="c-wishlist-button"
            :to="shelter.detailPagePath + '#shelter-wishlist'"
            quaternary-alt
          >
            {{ $t('shelters.overview.buttons.viewWishlist') }}
          </fn-button>
        </div>
      </div>
      <fn-pagination
        v-if="totalPageCount > 1"
        :current-page="currentPage"
        :total-pages="totalPageCount"
        query-parameter-key="page"
        @go-to-page="changePage"
      />
    </div>
  </div>
</template>

<script>
import fallbackImage from '@/assets/img/shelter_placeholder.png';

export default defineComponent({
  props: {
    id: {
      type: String,
      default: 'shelters-list',
    },
    title: {
      type: String,
      required: true,
    },
    shelters: {
      type: Array,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    totalPageCount: {
      type: Number,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['pageChange'],
  computed: {
    fallbackImage() {
      return fallbackImage;
    },
  },
  methods: {
    changePage(page) {
      this.$emit('pageChange', page);
    },
  },
});
</script>

<style lang="scss" scoped>
.shelters-list {
  .sl-header {
    display: flex;
    flex-direction: column;
    row-gap: $pad;
    column-gap: $pad;
    margin-bottom: 40px;

    @include bp(m) {
      flex-direction: row;
      justify-content: space-between;
    }

    .h-sort {
      @include bp(m) {
        flex-basis: 200px;

        :deep(span) {
          font-weight: normal;
        }
      }
    }
  }

  .sl-list {
    display: flex;
    flex-direction: column;
    row-gap: 64px;

    .shelter {
      display: grid;
      min-height: 280px;
      height: auto;

      @include bp(m) {
        column-gap: 40px;
        grid-template-columns: 356px 1fr;
      }

      .s-image {
        display: flex;
        justify-content: center;
        border: 1px solid palette(grey, pale);
        border-radius: $pad;
        overflow: hidden;
        height: 280px;
        margin-bottom: $pad * 2;
        width: 356px;
        max-height: 280px;

        @include bp(m) {
          height: auto;
          margin-bottom: 0;
        }

        @include bp(m, max) {
          margin-left: auto;
          margin-right: auto;
        }

        :deep(.picture-img) {
          object-fit: contain;
          height: 100%;
        }
      }

      .s-content {
        height: auto;
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        .c-text {
          margin: $space 0;
          max-height: 120px;
          width: auto;
          height: auto;
          display: -webkit-box;
          font-size: map-get($font-sizes, 'xl');
        }

        .c-wishlist-button {
          margin-top: $pad;
          display: flex;
          column-gap: math.div($pad, 2);
          border-width: 2px;
          justify-content: center;
          width: 100%;

          @include bp(m) {
            width: auto;
          }
        }
      }
    }
  }

  .trim {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 5;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
